<script setup lang="ts">
const localePath = useLocalePath()
const priceFormatter = usePriceFormatter()
const { t } = useI18n()
const router = useRouter()
const { ecommerce } = useAnalytics()
const activeOrderStore = useActiveOrderStore()
const uiStore = useUiStore()

async function handleClose() {
  uiStore.closeCartDrawer()
  await nextTick()
}

async function redirectToCheckout() {
  ecommerce.beginCheckout()
  await handleClose()
  await router.push(localePath('/checkout'))
}

const prizeMoney = usePrizeMoney()

const totalQuantity = computed(() => activeOrderStore.activeOrder.data?.totalQuantity ?? 0)
const activeOrderLines = computed(() => activeOrderStore.activeOrder.data?.lines ?? [])

// Computed property to calculate total savings
const totalSavings = computed(() => {
  if (!activeOrderStore.activeOrder.data?.lines) {
    return 0
  }
  return activeOrderStore.activeOrder.data.lines.reduce((total, line) => {
    const priceBeforeDiscount = line.productVariant.priceBeforeDiscount
    const price = line.unitPrice
    const quantity = line.quantity
    const savingsPerItem = (priceBeforeDiscount - price) * quantity
    return total + savingsPerItem
  }, 0)
})
</script>

<template>
  <NDrawer id="cart-drawer" slide-from="right" :show="uiStore.isCartDrawerOpen" :title="t('cart.label')" closable @close="handleClose">
    <div class="mt-8" data-testid="cartDrawer">
      <template v-if="activeOrderStore.activeOrder.data && totalQuantity">
        <div class="flow-root" data-testid="cartItemsList">
          <ul role="list" class="-my-6 divide-y divide-slate-200">
            <CartLineItem
              v-for="line in activeOrderLines"
              :key="line.id"
              :line
              :order="activeOrderStore.activeOrder.data"
              class="px4 py6 sm:px6"
              data-testid="cartLineItem"
              active-order
              @click:link="handleClose"
            />
          </ul>
        </div>
      </template>

      <div v-else class="h-48 flex items-center justify-center text-xl text-slate-400" data-testid="emptyCartMessage">
        {{ t('cart.empty.heading') }}
      </div>
    </div>

    <template v-if="totalQuantity" #footer>
      <div class="pb-safe" data-testid="cartFooter">
        <div class="bg-amber-200:35 px4 py2 hidden sm:flex sm:items-center sm:px6" data-testid="reservationWarning">
          <Icon name="i-ri:timer-flash-line" class="mr3 text-xl text-slate8 -ml1" />
          <span class="text-ce text-sm c-slate-800 fw600">
            {{ t('cart.items_not_reserved') }}
          </span>
        </div>

        <div class="pb6">
          <div class="divide-y n-divide-base">
            <ProductFeature
              icon="i-hugeicons:delivery-truck-01"
              :text="t('incentives.free_shipping.title').toUpperCase()"
              text-class="font-bold text-green-600"
              class="px4 py2 sm:px6"
            />
            <ProductFeature
              icon="i-heroicons:check-badge"
              :text="t('shipping.within_24_hours')"
              class="px4 py2 sm:px6"
            />
            <ProductFeature
              icon="i-heroicons:gift-top"
              :text="t('promotion.purchase_competition_prizes', [prizeMoney])"
              class="px4 py2 sm:px6"
            />
            <ProductFeature
              icon="i-hugeicons:delivery-return-02"
              :text="t('trustable.productpage.return_xy_days', [$config.public.returnWindowDays])"
              class="px4 py2 sm:px6"
            />
          </div>

          <div class="border-t n-border-base">
            <div v-if="totalSavings > 0" class="flex justify-between bg-red-600 px4 py2 text-white fw600 sm:px6">
              <p v-text="t('order.summary.you_save.label')" />
              <p v-if="activeOrderStore.activeOrder.data?.subTotal">
                {{ priceFormatter(totalSavings, activeOrderStore.activeOrder.data!.currency) }}
              </p>
            </div>
          </div>
          <div class="border-t n-border-base">
            <div class="flex justify-between px4 pt4 text-base text-slate-900 fw600 sm:px6">
              <p data-testid="subtotalLabel" v-text="t('order.summary.subtotal.label')" />
              <p v-if="activeOrderStore.activeOrder.data?.subTotal" data-testid="subtotalAmount">
                {{ priceFormatter(activeOrderStore.activeOrder.data.subTotal, activeOrderStore.activeOrder.data.currency) }}
              </p>
            </div>
          </div>

          <div class="mt-6 px4 sm:px6">
            <UiButton
              size="xl"
              class="w-full justify-center rd-md fw600 leading-10 op100"
              data-testid="checkoutButton"
              @click="redirectToCheckout"
            >
              {{ t('checkout.label') }}
            </UiButton>
          </div>
        </div>
      </div>
    </template>
  </NDrawer>
</template>
